import classnames from "classnames";
import styles from "./HeroGallery.scss";
import ImageGallery from "../imageGallery/ImageGallery";
import Image from "../image/Image";
import ZoomableImage from "../zoomableImage/ZoomableImage";
import { ZoomableImageProvider } from "../zoomableImage/ZoomableImageContext";
import { useRuntimeConstants } from "../../context/runtimeConstants/RuntimeConstantsContext";
import { useViewport } from "../../context/viewport/ViewportContext";
import { SMALL_VIEWPORT } from "../../constants";
import {
  ImageGalleryProvider,
  useImageGalleryContext,
} from "../imageGallery/ImageGalleryContext";
import { useMemo } from "react";
import {
  GalleryImage,
  MapImageArgs,
} from "../../types/components/imageGallery";
import HeroGalleryBuyTheLook from "./HeroGalleryBuyTheLook";
import HeroGalleryBadges from "./HeroGalleryBadges";
import {
  HeroGalleryVideoProvider,
  useHeroGalleryVideo,
} from "./HeroGalleryVideoContext";
import HeroGalleryVideoButton from "./HeroGalleryVideoButton";
import HeroGalleryVideoPlayer from "./HeroGalleryVideoPlayer";
import ModelWears from "../modelWears/ModelWears";
import ReleaseDateBanner from "../releaseDateBanner/ReleaseDateBanner";
import ViewportOnly from "../../containers/viewportOnly/ViewportOnly";
import { useVariant } from "../../context/variant/VariantContext";
import AiImageDisclosure from "../aiImageDisclosure/AiImageDisclosure";
import getImagesForColourWay from "./getImagesForColourWay";
import resetClippedContainerScroll from "../../utils/resetClippedContainerScroll";

const HERO_IMAGE_TIMING_ID = "pdp:hero_image_displayed";

const THUMBNAIL_WIDTH = 44;
const THUMBNAIL_INSTRUCTION_ID = "heroGalleryThumbnailInstruction";

function HeroGalleryContent() {
  const { product, getTranslation } = useRuntimeConstants() as {
    product: { name: string };
    getTranslation: (
      key: string,
      variables?: Record<string, unknown>,
    ) => string;
  };
  const { viewportCategory } = useViewport();
  const { images, displayedImageIndex, setDisplayedImageIndex } =
    useImageGalleryContext();
  const { closeVideo, isVideoVisible } = useHeroGalleryVideo();

  return (
    <div
      className={styles.heroGallery}
      role="region"
      aria-label={getTranslation("pdp_media_gallery_label")}
      data-testid="heroGallery"
      tabIndex={-1}
    >
      <ViewportOnly medium large>
        <div
          className={styles.tiles}
          style={
            {
              "--thumbnail-image-width": `${THUMBNAIL_WIDTH}px`,
            } as React.CSSProperties
          }
        >
          <p
            id={THUMBNAIL_INSTRUCTION_ID}
            className={styles.screenReaderOnly}
            aria-hidden="true"
          >
            {getTranslation("pdp_media_gallery_thumbnail_instruction")}
          </p>
          <ul
            className={styles.thumbnails}
            aria-describedby={THUMBNAIL_INSTRUCTION_ID}
            aria-label={getTranslation("pdp_media_gallery_thumbnail_label")}
            data-testid="heroThumbnails"
          >
            {images.map((image, index) => {
              const isCurrentImage =
                index === displayedImageIndex && !isVideoVisible;

              return (
                <li key={`${image.url}-${index}`}>
                  <button
                    className={classnames(styles.thumbnailButton, {
                      [styles.active]: isCurrentImage,
                    })}
                    onClick={() => {
                      setDisplayedImageIndex(index);
                      closeVideo();
                    }}
                    aria-current={isCurrentImage ? "true" : undefined}
                  >
                    <Image
                      loadingType="eager"
                      alternateText={getTranslation(
                        "pdp_media_gallery_thumbnail_count",
                        { index: index + 1, total: images.length },
                      )}
                      url={image.url}
                      width={THUMBNAIL_WIDTH}
                      sizes={`${THUMBNAIL_WIDTH}px`}
                      type="thumbnail"
                      additionalClasses={styles.thumbnailImage}
                    />
                  </button>
                </li>
              );
            })}
          </ul>
          <div className={styles.actions}>
            <HeroGalleryVideoButton />
            <HeroGalleryBuyTheLook />
          </div>
        </div>
      </ViewportOnly>

      <div className={styles.gallery} onScroll={resetClippedContainerScroll}>
        <div className={styles.media}>
          <div
            className={isVideoVisible ? styles.hidden : undefined}
            role="region"
            aria-label={getTranslation("pdp_media_gallery_carousel_label")}
          >
            <div className={styles.imageGalleryContainer}>
              <ImageGallery
                mapImage={getImageMapper({
                  productName: product.name,
                  getTranslation,
                  imageCount: images.length,
                })}
                screenHoverable={viewportCategory !== SMALL_VIEWPORT}
                fullHeightContainer={false}
              />

              <HeroGalleryBadges className={styles.badge} />
            </div>
          </div>

          <HeroGalleryVideoPlayer />
        </div>

        <ModelWears />

        <ViewportOnly small>
          <ReleaseDateBanner />
          <div className={styles.mobileActions}>
            <HeroGalleryBuyTheLook />
            <HeroGalleryVideoButton />
          </div>
        </ViewportOnly>
      </div>
    </div>
  );
}

function getImageMapper({
  productName,
  getTranslation,
  imageCount,
}: {
  productName: string;
  getTranslation: (key: string, variables?: Record<string, unknown>) => string;
  imageCount: number;
}) {
  return ({
    image,
    index,
    displayedImageIndex,
    ariaHiddenOverride,
  }: MapImageArgs) => {
    const alt = `${productName}, ${getTranslation("pdp_misc_count", {
      index: index + 1,
      total: imageCount,
    })}`;

    const ariaHidden = ariaHiddenOverride || index !== displayedImageIndex;

    return (
      <AiImageDisclosure image={image} ariaHidden={ariaHidden}>
        <ZoomableImage
          additionalClasses={styles.image}
          loadingType={index === 0 ? "eager" : "lazy"}
          url={image.url}
          width={513}
          zoomScaleLevels={[0.5, 1]}
          zoomWidth={1926}
          sizes="(max-width: 720px) 100vw, (min-width: 720px) 513px"
          type="hero"
          ariaHidden={ariaHidden}
          alternateText={alt}
          index={index}
          dataTimingId={
            index === 0 && !ariaHiddenOverride
              ? HERO_IMAGE_TIMING_ID
              : undefined
          }
        />
      </AiImageDisclosure>
    );
  };
}

const MAX_GALLERY_IMAGES = 7;

export default function HeroGallery() {
  const { product } = useRuntimeConstants() as {
    product: { images: GalleryImage[] };
  };
  const { selectedVariant } = useVariant() as {
    selectedVariant?: { colourWayId?: number };
  };

  const images = useMemo(
    () =>
      getImagesForColourWay(product.images, selectedVariant?.colourWayId).slice(
        0,
        MAX_GALLERY_IMAGES,
      ),
    [product.images, selectedVariant?.colourWayId],
  );

  return (
    <HeroGalleryVideoProvider>
      <ImageGalleryProvider images={images}>
        <ZoomableImageProvider>
          <HeroGalleryContent />
        </ZoomableImageProvider>
      </ImageGalleryProvider>
    </HeroGalleryVideoProvider>
  );
}
